import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BeltContext = createContext(null);

/**
 * BeltProvider - Provides belt configuration data throughout the app
 *
 * Features:
 * - Fetches belt configs from /api/belts on mount
 * - Provides belt lookup by slug
 * - Provides belt comparison function
 * - Provides options for select dropdowns
 * - Generates gradient CSS from color stops
 */
export function BeltProvider({ children }) {
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch belts from API
  const fetchBelts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/belts`);
      if (response.ok) {
        const data = await response.json();
        setBelts(data);
        setError(null);
      } else {
        throw new Error('Failed to fetch belts');
      }
    } catch (err) {
      console.error('Error fetching belts:', err);
      setError(err.message);
      // Fallback to default belts if API fails
      setBelts(getDefaultBelts());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBelts();
  }, [fetchBelts]);

  // Create a map for O(1) slug lookups
  const beltMap = useMemo(() => {
    const map = {};
    belts.forEach(belt => {
      map[belt.slug] = belt;
    });
    return map;
  }, [belts]);

  // Create belt order map for comparisons
  const beltOrder = useMemo(() => {
    const order = {};
    belts.forEach(belt => {
      order[belt.slug] = belt.order;
    });
    return order;
  }, [belts]);

  // Get belt by slug
  const getBeltBySlug = useCallback((slug) => {
    return beltMap[slug] || null;
  }, [beltMap]);

  // Compare two belts (-1, 0, 1)
  const compareBelts = useCallback((slug1, slug2) => {
    const order1 = beltOrder[slug1] ?? 0;
    const order2 = beltOrder[slug2] ?? 0;
    if (order1 < order2) return -1;
    if (order1 > order2) return 1;
    return 0;
  }, [beltOrder]);

  // Get options for select dropdowns
  const getBeltOptions = useCallback(() => {
    return belts.map(belt => ({
      value: belt.slug,
      label: belt.display_name,
    }));
  }, [belts]);

  // Generate CSS gradient from color stops
  const generateGradient = useCallback((colors) => {
    if (!colors || colors.length === 0) return '#808080';
    if (colors.length === 1) return colors[0].color;
    // Check if it's essentially a solid color (same color at start and end)
    if (colors.length === 2 && colors[0].color === colors[1].color) {
      return colors[0].color;
    }
    const stops = colors.map(c => `${c.color} ${c.position * 100}%`);
    return `linear-gradient(to right, ${stops.join(', ')})`;
  }, []);

  // Get CSS styles for a belt
  const getBeltStyles = useCallback((slug) => {
    const belt = beltMap[slug];
    if (!belt) {
      return {
        background: '#808080',
        borderColor: '#666666',
        color: '#FFFFFF',
      };
    }
    return {
      background: generateGradient(belt.colors),
      borderColor: belt.border_color,
      color: belt.text_color,
      stripeColor: belt.stripe_color,
    };
  }, [beltMap, generateGradient]);

  // Get belt name with stripes
  const getBeltName = useCallback((slug, stripes = 0) => {
    const belt = beltMap[slug];
    if (!belt) return 'Unknown Belt';

    if (stripes > 0) {
      const stripeText = stripes === 1 ? '1 Stripe' : `${stripes} Stripes`;
      return `${belt.short_name} (${stripeText})`;
    }
    return belt.short_name;
  }, [beltMap]);

  // Get badge colors for chips/tags (simpler bg/text pair)
  const getBeltBadgeColors = useCallback((slug) => {
    const belt = beltMap[slug];
    if (!belt) {
      return { bg: '#808080', text: '#FFFFFF' };
    }
    // For badge, use first color as background
    const bgColor = belt.colors?.[0]?.color || '#808080';
    return {
      bg: bgColor,
      text: belt.text_color,
      border: belt.border_color,
    };
  }, [beltMap]);

  // Refresh belts (for admin after changes)
  const refreshBelts = useCallback(() => {
    return fetchBelts();
  }, [fetchBelts]);

  const value = {
    belts,
    loading,
    error,
    getBeltBySlug,
    compareBelts,
    getBeltOptions,
    generateGradient,
    getBeltStyles,
    getBeltName,
    getBeltBadgeColors,
    refreshBelts,
    beltOrder,
  };

  return (
    <BeltContext.Provider value={value}>
      {children}
    </BeltContext.Provider>
  );
}

/**
 * useBelts hook - Access belt context
 */
export function useBelts() {
  const context = useContext(BeltContext);
  if (!context) {
    throw new Error('useBelts must be used within a BeltProvider');
  }
  return context;
}

/**
 * Default belts fallback if API fails
 */
function getDefaultBelts() {
  return [
    { slug: 'white', display_name: 'White Belt', short_name: 'White', order: 0, colors: [{ color: '#FFFFFF', position: 0 }, { color: '#FFFFFF', position: 1 }], border_color: '#9CA3AF', text_color: '#1F2937', stripe_color: '#4B5563', is_black_belt: false },
    { slug: 'yellow', display_name: 'Yellow Belt', short_name: 'Yellow', order: 1, colors: [{ color: '#FACC15', position: 0 }, { color: '#FACC15', position: 1 }], border_color: '#EAB308', text_color: '#713F12', stripe_color: '#1F2937', is_black_belt: false },
    { slug: 'orange', display_name: 'Orange Belt', short_name: 'Orange', order: 2, colors: [{ color: '#F97316', position: 0 }, { color: '#F97316', position: 1 }], border_color: '#EA580C', text_color: '#FFF7ED', stripe_color: '#1F2937', is_black_belt: false },
    { slug: 'green', display_name: 'Green Belt', short_name: 'Green', order: 3, colors: [{ color: '#16A34A', position: 0 }, { color: '#16A34A', position: 1 }], border_color: '#15803D', text_color: '#DCFCE7', stripe_color: '#1F2937', is_black_belt: false },
    { slug: 'blue', display_name: 'Blue Belt', short_name: 'Blue', order: 4, colors: [{ color: '#2563EB', position: 0 }, { color: '#2563EB', position: 1 }], border_color: '#1D4ED8', text_color: '#DBEAFE', stripe_color: '#1F2937', is_black_belt: false },
    { slug: 'purple', display_name: 'Purple Belt', short_name: 'Purple', order: 5, colors: [{ color: '#9333EA', position: 0 }, { color: '#9333EA', position: 1 }], border_color: '#7E22CE', text_color: '#F3E8FF', stripe_color: '#1F2937', is_black_belt: false },
    { slug: 'brown', display_name: 'Brown Belt', short_name: 'Brown', order: 6, colors: [{ color: '#92400E', position: 0 }, { color: '#92400E', position: 1 }], border_color: '#78350F', text_color: '#FFFBEB', stripe_color: '#1F2937', is_black_belt: false },
    { slug: 'black_1', display_name: '1st Dan Black Belt', short_name: 'Black 1st Dan', order: 7, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 1 },
    { slug: 'black_2', display_name: '2nd Dan Black Belt', short_name: 'Black 2nd Dan', order: 8, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 2 },
    { slug: 'black_3', display_name: '3rd Dan Black Belt', short_name: 'Black 3rd Dan', order: 9, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 3 },
    { slug: 'black_4', display_name: '4th Dan Black Belt', short_name: 'Black 4th Dan', order: 10, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 4 },
    { slug: 'black_5', display_name: '5th Dan Black Belt', short_name: 'Black 5th Dan', order: 11, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 5 },
    { slug: 'black_6', display_name: '6th Dan Black Belt', short_name: 'Black 6th Dan', order: 12, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 6 },
    { slug: 'black_7', display_name: '7th Dan Black Belt', short_name: 'Black 7th Dan', order: 13, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 7 },
    { slug: 'black_8', display_name: '8th Dan Black Belt', short_name: 'Black 8th Dan', order: 14, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 8 },
    { slug: 'black_9', display_name: '9th Dan Black Belt', short_name: 'Black 9th Dan', order: 15, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 9 },
    { slug: 'black_10', display_name: '10th Dan Black Belt', short_name: 'Black 10th Dan', order: 16, colors: [{ color: '#000000', position: 0 }, { color: '#000000', position: 1 }], border_color: '#374151', text_color: '#FFFFFF', stripe_color: '#EAB308', is_black_belt: true, degree: 10 },
  ];
}

export default BeltContext;
